/**
 * Damage dice (Foundry: DamageDicePF2e) and damage alterations (Foundry:
 * rule-element/damage-alteration/alteration.ts): extra dice a DamageDice Rule Element adds to a
 * damage roll, and the changes a DamageAlteration makes to a modifier's or dice's type, faces,
 * number or tags. Both are deferred until a strike's damage is calculated with its own options.
 */
import type { EngineItem } from "./items";
import { modifierRollOptions, type Modifier } from "./modifiers";
import { testPredicate, type RawPredicate } from "./predicate";
import { sluggify } from "./slug";
import { DAMAGE_TYPE_CATEGORIES, isDamageDiceFaces, isDamageType, nextDieSize, type DamageDieSize } from "./values";

export interface DamageDiceOverride {
  upgrade?: boolean;
  downgrade?: boolean;
  dieSize?: DamageDieSize;
  damageType?: string;
  diceNumber?: number;
}

export interface DamageDice {
  selector: string;
  slug: string;
  label: string;
  diceNumber: number;
  dieSize: DamageDieSize | null;
  /** true: critical only, not doubled; false: never doubled; null: doubled on a critical hit. */
  critical: boolean | null;
  category: "persistent" | "precision" | "splash" | null;
  damageType: string | null;
  tags: string[];
  override: DamageDiceOverride | null;
  ignored: boolean;
  enabled: boolean;
  predicate: RawPredicate;
  /** Roll options of the Entry that produced these dice, added to every predicate test. */
  parentOptions: string[];
  alterations: DamageAlteration[];
  hideIfDisabled: boolean;
  source: string | null;
  sourceName: string | null;
}

export interface DamageDiceParams {
  selector: string;
  slug?: string;
  label?: string;
  diceNumber?: number;
  dieSize?: DamageDieSize | null;
  critical?: boolean | null;
  category?: string | null;
  damageType?: string | null;
  tags?: string[];
  override?: DamageDiceOverride | null;
  predicate?: RawPredicate;
  parentOptions?: string[];
  enabled?: boolean;
  ignored?: boolean;
  alterations?: DamageAlteration[];
  hideIfDisabled?: boolean;
  source?: string | null;
  sourceName?: string | null;
}

export function createDamageDice(params: DamageDiceParams): DamageDice {
  const label = params.label ?? "";
  const slug = sluggify(params.slug ?? label);
  const damageType = params.damageType ?? null;
  const category = params.category === "persistent" || params.category === "precision" || params.category === "splash" ? params.category : damageType === "bleed" ? "persistent" : null;
  const predicate = params.predicate ?? [];
  const enabled = params.enabled ?? testPredicate(predicate, new Set());
  return {
    selector: params.selector,
    slug,
    label,
    diceNumber: params.diceNumber ?? 0,
    dieSize: params.dieSize ?? null,
    critical: category === "splash" ? !!params.critical : (params.critical ?? null),
    category,
    damageType,
    tags: [...new Set(params.tags ?? [])],
    override: params.override ? { ...params.override } : null,
    ignored: params.ignored ?? !enabled,
    enabled,
    predicate,
    parentOptions: params.parentOptions ?? [],
    alterations: [...(params.alterations ?? [])],
    hideIfDisabled: params.hideIfDisabled ?? false,
    source: params.source ?? null,
    sourceName: params.sourceName ?? null,
  };
}

export function cloneDamageDice(dice: DamageDice): DamageDice {
  return { ...dice, tags: [...dice.tags], override: dice.override ? { ...dice.override } : null, predicate: structuredClone(dice.predicate), alterations: [...dice.alterations], parentOptions: [...dice.parentOptions] };
}

/** Test the predicate against a set of roll options (Foundry: DamageDicePF2e#test). */
export function testDamageDice(dice: DamageDice, options: Set<string>): void {
  const domain = dice.parentOptions.length > 0 ? new Set([...options, ...dice.parentOptions]) : options;
  dice.enabled = testPredicate(dice.predicate, domain);
  dice.ignored = !dice.enabled;
}

/** Roll options describing a set of dice, `dice:`-prefixed (Foundry: DamageDicePF2e#getRollOptions). */
export function damageDiceRollOptions(dice: DamageDice): string[] {
  const damageKind = dice.selector.endsWith("healing") ? "healing" : "damage";
  const categoryFromType = DAMAGE_TYPE_CATEGORIES[dice.damageType ?? "untyped"] ?? null;
  return [
    damageKind,
    `dice:slug:${dice.slug}`,
    `dice:number:${dice.diceNumber}`,
    `dice:faces:${dice.dieSize}`,
    `dice:${damageKind}`,
    ...(dice.category ? [`${damageKind}:category:${dice.category}`, `dice:${damageKind}:category:${dice.category}`] : []),
    ...(categoryFromType ? [`${damageKind}:category:${categoryFromType}`, `dice:${damageKind}:category:${categoryFromType}`] : []),
    ...(dice.damageType ? [`${damageKind}:type:${dice.damageType}`, `dice:${damageKind}:type:${dice.damageType}`] : []),
    ...dice.tags.flatMap((t) => [`${damageKind}:tag:${t}`, `dice:tag:${t}`]),
  ];
}

/* ------------------------------- Damage alterations ------------------------------- */

export type DamageAlterationProperty = "damage-type" | "dice-faces" | "dice-number" | "tags";
export type DamageAlterationMode = "add" | "subtract" | "remove" | "multiply" | "downgrade" | "upgrade" | "override";
export type DamageAlterationValue = string | number | string[] | null;

/** A DamageAlteration Rule Element's deferred change, applied to a modifier or dice at damage time. */
export interface DamageAlteration {
  /** The slug of the modifier or dice it applies to; null applies to all of the selector. */
  slug: string | null;
  property: DamageAlterationProperty;
  mode: DamageAlterationMode;
  /** Resolve the value with the item producing the damage as a resolvable (`@weapon`, `@spell`). */
  resolve: (item: EngineItem | null) => DamageAlterationValue;
  predicate: RawPredicate;
  parentOptions: string[];
  /** Whether the producing Rule Element has been ignored since. */
  isIgnored: () => boolean;
  source: string | null;
  sourceName: string | null;
  relabel: string | null;
}

interface AlterableTarget {
  /** Dice have a diceNumber and dieSize; modifiers a value. */
  diceNumber?: number;
  dieSize?: DamageDieSize | null;
  value?: number;
  damageType: string | null;
  tags: string[];
  override?: DamageDiceOverride | null;
  label: string;
}

function getNewNumber(mode: DamageAlterationMode, current: number, change: number): number {
  switch (mode) {
    case "multiply": return Math.trunc(current * change);
    case "add": return current + change;
    case "subtract":
    case "remove": return current - change;
    case "downgrade": return Math.min(current, change);
    case "upgrade": return Math.max(current, change);
    case "override": return change;
  }
}

/** The new value the alteration produces for the target, or null when it does not apply (Foundry: DamageAlteration#getNewValue). */
export function alterationNewValue(alteration: DamageAlteration, target: AlterableTarget, item: EngineItem | null): DamageAlterationValue {
  const change = alteration.resolve(item);
  if (alteration.isIgnored()) return null;
  switch (alteration.property) {
    case "damage-type":
      return isDamageType(change) ? change : null;
    case "dice-number":
      return "diceNumber" in target && typeof change === "number" ? Math.min(99, Math.max(0, Math.floor(getNewNumber(alteration.mode, target.diceNumber ?? 0, change)))) : null;
    case "dice-faces": {
      if (!("dieSize" in target) || !target.dieSize) return null;
      const currentFaces = Number(target.dieSize.replace("d", ""));
      if (alteration.mode === "downgrade") return isDamageDiceFaces(change) ? Math.min(change, currentFaces) : Number(nextDieSize(target.dieSize, "downgrade").replace("d", ""));
      if (alteration.mode === "upgrade") return isDamageDiceFaces(change) ? Math.max(change, currentFaces) : Number(nextDieSize(target.dieSize, "upgrade").replace("d", ""));
      if (alteration.mode === "override" && isDamageDiceFaces(change)) return change;
      return null;
    }
    case "tags": {
      const changes = (Array.isArray(change) ? change : [change]).filter((t): t is string => typeof t === "string");
      const tags = [...target.tags];
      switch (alteration.mode) {
        case "add": return [...tags, ...changes];
        case "remove":
        case "subtract": return tags.filter((t) => !changes.includes(t));
        case "override": return changes;
        default: return null;
      }
    }
  }
}

export interface ApplyAlterationOptions {
  item: EngineItem | null;
  test: Set<string> | string[];
  /** A weapon whose base die may be upgraded only once (Foundry's `damageFacesUpgraded` flag). */
  facesUpgraded?: { value: boolean } | null;
}

/** Apply one alteration to dice or a modifier in place (Foundry: DamageAlteration#applyTo). Returns whether it changed anything. */
export function applyDamageAlteration(alteration: DamageAlteration, target: DamageDice | Modifier, options: ApplyAlterationOptions): boolean {
  if (alteration.isIgnored()) return false;
  const isModifier = "value" in target && !("diceNumber" in target);
  if (isModifier && (alteration.property === "dice-faces" || alteration.property === "dice-number")) return false;
  const dice = isModifier ? null : (target as DamageDice);
  if (dice && !dice.damageType && !dice.diceNumber && !dice.dieSize && dice.override) return false;
  const ownOptions = dice ? damageDiceRollOptions(dice) : modifierRollOptions(target as Modifier);
  const passes = alteration.predicate.length === 0 || testPredicate(alteration.predicate, new Set([...options.test, ...ownOptions, ...alteration.parentOptions]));
  if (!passes) return false;
  const value = alterationNewValue(alteration, target as AlterableTarget, options.item);
  if (alteration.property === "tags" && Array.isArray(value)) {
    target.tags = value;
  } else if (typeof value === "string") {
    target.damageType = value;
    if (isModifier && value === "bleed") (target as Modifier).damageCategory = "persistent";
  } else if (alteration.property === "dice-faces" && dice && isDamageDiceFaces(value)) {
    const size = `d${value}` as DamageDieSize;
    if (alteration.slug === "base" && alteration.mode === "upgrade" && dice.dieSize !== size && options.facesUpgraded) {
      if (options.facesUpgraded.value) return false;
      options.facesUpgraded.value = true;
    }
    dice.dieSize = size;
  } else if (alteration.property === "dice-number" && dice && typeof value === "number") {
    dice.diceNumber = value;
  } else {
    return false;
  }
  if (alteration.relabel) target.label = alteration.relabel;
  return true;
}
